import type { BlockType } from '../../../types'

export interface LayoutTemplate {
  id: string
  name: string
  description: string
  thumbnail: string  // emoji for visual identifier
  blocks: Array<{ type: BlockType; props: Record<string, string | number | boolean> }>
}

export const LAYOUT_TEMPLATES: LayoutTemplate[] = [
  {
    id: 'simple-upsell',
    name: 'Simple Upsell',
    description: 'Clean, minimal upsell with hero, product offer, and action buttons',
    thumbnail: 'Zap',
    blocks: [
      {
        type: 'hero',
        props: {
          headline: 'Wait! Special Offer Just For You',
          subheadline: 'Add this exclusive product to your order at a discounted price',
          imageUrl: '',
          bgColor: '#f8f9fa',
        },
      },
      {
        type: 'product-offer',
        props: {
          productName: 'Premium Add-On Product',
          originalPrice: '49.99',
          discountedPrice: '29.99',
          discountLabel: '40% OFF',
          imageUrl: '',
          showBadge: true,
        },
      },
      {
        type: 'cta',
        props: {
          acceptText: 'Yes! Add to my order',
          declineText: "No thanks, I'll pass",
          acceptColor: '#22c55e',
          declineColor: '#ef4444',
        },
      },
    ],
  },
  {
    id: 'social-proof-upsell',
    name: 'Social Proof Upsell',
    description: 'Build trust with testimonials and urgency timer',
    thumbnail: 'Star',
    blocks: [
      {
        type: 'hero',
        props: {
          headline: 'Before You Go - A Special Gift!',
          subheadline: 'Thousands of customers love this premium upgrade',
          imageUrl: '',
          bgColor: '#f8f9fa',
        },
      },
      {
        type: 'testimonial',
        props: {
          quote: '"This product exceeded all my expectations! I wish I had added it to my order from the start."',
          author: 'Sarah M., Verified Customer',
          rating: 5,
        },
      },
      {
        type: 'product-offer',
        props: {
          productName: 'Customer Favorite Add-On',
          originalPrice: '59.99',
          discountedPrice: '34.99',
          discountLabel: '42% OFF',
          imageUrl: '',
          showBadge: true,
        },
      },
      {
        type: 'timer',
        props: {
          minutes: 5,
          message: 'This exclusive offer expires in:',
          bgColor: '#fef3c7',
        },
      },
      {
        type: 'cta',
        props: {
          acceptText: 'Yes! Add to my order',
          declineText: 'No thanks',
          acceptColor: '#22c55e',
          declineColor: '#ef4444',
        },
      },
    ],
  },
  {
    id: 'feature-rich',
    name: 'Feature-Rich',
    description: 'Complete sales page with features, benefits, and social proof',
    thumbnail: 'Target',
    blocks: [
      {
        type: 'hero',
        props: {
          headline: 'Upgrade Your Order with Our Best-Selling Product',
          subheadline: 'Limited time offer - save big when you add now',
          imageUrl: '',
          bgColor: '#f8f9fa',
        },
      },
      {
        type: 'feature-list',
        props: {
          feature1: 'Premium quality materials - built to last',
          feature2: '30-day money back guarantee - risk free',
          feature3: 'Free shipping included with your order',
          iconColor: '#22c55e',
        },
      },
      {
        type: 'product-offer',
        props: {
          productName: 'Premium Bundle Package',
          originalPrice: '79.99',
          discountedPrice: '44.99',
          discountLabel: 'SAVE $35',
          imageUrl: '',
          showBadge: true,
        },
      },
      {
        type: 'discount-badge',
        props: {
          text: 'LIMITED TIME: SAVE 44%',
          bgColor: '#ef4444',
          textColor: '#ffffff',
        },
      },
      {
        type: 'testimonial',
        props: {
          quote: '"Worth every penny! The quality is outstanding and it pairs perfectly with my original purchase."',
          author: 'Michael R.',
          rating: 5,
        },
      },
      {
        type: 'cta',
        props: {
          acceptText: 'Add to Order - Save $35',
          declineText: 'Continue without upgrade',
          acceptColor: '#22c55e',
          declineColor: '#ef4444',
        },
      },
    ],
  },
  {
    id: 'media-landing',
    name: 'Media Landing',
    description: 'Visual storytelling with video and image gallery',
    thumbnail: 'Film',
    blocks: [
      {
        type: 'hero',
        props: {
          headline: 'See Why Customers Love This Product',
          subheadline: 'Watch the demonstration and view real customer photos',
          imageUrl: '',
          bgColor: '#f8f9fa',
        },
      },
      {
        type: 'video-embed',
        props: {
          videoUrl: '',
          autoplay: false,
          aspectRatio: '16:9',
        },
      },
      {
        type: 'image-gallery',
        props: {
          imageUrl1: '',
          imageUrl2: '',
          imageUrl3: '',
          columns: 3,
          gap: 8,
        },
      },
      {
        type: 'product-offer',
        props: {
          productName: 'As Seen in Video - Special Offer',
          originalPrice: '54.99',
          discountedPrice: '32.99',
          discountLabel: '40% OFF',
          imageUrl: '',
          showBadge: true,
        },
      },
      {
        type: 'cta',
        props: {
          acceptText: 'Add This to My Order',
          declineText: 'No thanks',
          acceptColor: '#22c55e',
          declineColor: '#ef4444',
        },
      },
    ],
  },
  {
    id: 'urgency-sale',
    name: 'Urgency Sale',
    description: 'High-conversion template with scarcity and urgency signals',
    thumbnail: 'Flame',
    blocks: [
      {
        type: 'discount-badge',
        props: {
          text: 'FLASH SALE - 50% OFF',
          bgColor: '#ef4444',
          textColor: '#ffffff',
        },
      },
      {
        type: 'hero',
        props: {
          headline: 'Last Chance! Don\'t Miss This Deal',
          subheadline: 'This exclusive upgrade is only available right now',
          imageUrl: '',
          bgColor: '#fef3c7',
        },
      },
      {
        type: 'timer',
        props: {
          minutes: 3,
          message: 'Offer expires in:',
          bgColor: '#fef3c7',
        },
      },
      {
        type: 'product-offer',
        props: {
          productName: 'Limited Time Flash Deal',
          originalPrice: '69.99',
          discountedPrice: '34.99',
          discountLabel: '50% OFF',
          imageUrl: '',
          showBadge: true,
        },
      },
      {
        type: 'cta',
        props: {
          acceptText: 'Claim This Deal Now',
          declineText: 'I\'ll pay full price later',
          acceptColor: '#ef4444',
          declineColor: '#71717a',
        },
      },
      {
        type: 'testimonial',
        props: {
          quote: '"I almost skipped this offer - so glad I didn\'t! Best decision ever."',
          author: 'Jennifer L.',
          rating: 5,
        },
      },
    ],
  },
  {
    id: 'landing-product-spotlight',
    name: 'Landing: Product Spotlight',
    description: 'PDP-style landing with gallery, specs, review widget, and CTA section',
    thumbnail: 'ShoppingBag',
    blocks: [
      {
        type: 'hero',
        props: {
          headline: 'A Better Add-On For Your Purchase',
          subheadline: 'See details, compare features, and claim your discounted upgrade',
          imageUrl: '',
          bgColor: '#f8f9fa',
        },
      },
      {
        type: 'product-gallery',
        props: {
          imageUrl1: '',
          imageUrl2: '',
          imageUrl3: '',
          columns: 3,
          gap: 8,
        },
      },
      {
        type: 'product-specs',
        props: {
          feature1: 'Made with premium materials',
          feature2: 'Compatible with your current order',
          feature3: 'Backed by 30-day guarantee',
          iconColor: '#22c55e',
        },
      },
      {
        type: 'reviews-widget',
        props: {
          quote: '"I added this at checkout and use it every day."',
          author: 'Amanda T.',
          rating: 5,
        },
      },
      {
        type: 'cta-section',
        props: {
          acceptText: 'Yes, add this to my order',
          declineText: 'Skip this upgrade',
          acceptColor: '#22c55e',
          declineColor: '#71717a',
        },
      },
    ],
  },
  {
    id: 'landing-comparison',
    name: 'Landing: Comparison',
    description: 'Highlights product value with specs + social proof before final CTA',
    thumbnail: 'Scale',
    blocks: [
      {
        type: 'hero',
        props: {
          headline: 'Compare and Upgrade in One Click',
          subheadline: 'Everything your current order has, plus more value for less',
          imageUrl: '',
          bgColor: '#eef6ff',
        },
      },
      {
        type: 'product-offer',
        props: {
          productName: 'Upgrade Bundle',
          originalPrice: '79.99',
          discountedPrice: '44.99',
          discountLabel: 'SAVE $35',
          imageUrl: '',
          showBadge: true,
        },
      },
      {
        type: 'product-specs',
        props: {
          feature1: 'Higher durability than standard option',
          feature2: 'Priority shipping included',
          feature3: 'Extended warranty included',
          iconColor: '#0ea5e9',
        },
      },
      {
        type: 'reviews-widget',
        props: {
          quote: '"The upgrade was absolutely worth it."',
          author: 'Carlos M.',
          rating: 5,
        },
      },
      {
        type: 'cta-section',
        props: {
          acceptText: 'Upgrade my order now',
          declineText: 'Keep current order',
          acceptColor: '#0ea5e9',
          declineColor: '#71717a',
        },
      },
    ],
  },
  {
    id: 'landing-social-proof',
    name: 'Landing: Social Proof',
    description: 'Review-heavy landing page with urgency and CTA section',
    thumbnail: 'MessageCircle',
    blocks: [
      {
        type: 'hero',
        props: {
          headline: 'Customers Love This Upgrade',
          subheadline: 'Join thousands of shoppers who added this to their order',
          imageUrl: '',
          bgColor: '#fff7ed',
        },
      },
      {
        type: 'reviews-widget',
        props: {
          quote: '"Exactly what I needed. Delivery was super fast."',
          author: 'Julia R.',
          rating: 5,
        },
      },
      {
        type: 'timer',
        props: {
          minutes: 6,
          message: 'Discount ends in:',
          bgColor: '#fef3c7',
        },
      },
      {
        type: 'product-gallery',
        props: {
          imageUrl1: '',
          imageUrl2: '',
          imageUrl3: '',
          columns: 3,
          gap: 8,
        },
      },
      {
        type: 'cta-section',
        props: {
          acceptText: 'Claim this offer',
          declineText: 'No thanks',
          acceptColor: '#ea580c',
          declineColor: '#71717a',
        },
      },
    ],
  },
  {
    id: 'popup-timer-fast',
    name: 'Popup: Timed Offer',
    description: 'Compact popup optimized for timer-based trigger',
    thumbnail: 'Timer',
    blocks: [
      {
        type: 'discount-badge',
        props: {
          text: 'LIMITED OFFER',
          bgColor: '#ef4444',
          textColor: '#ffffff',
        },
      },
      {
        type: 'product-offer',
        props: {
          productName: 'Popup Upgrade',
          originalPrice: '39.99',
          discountedPrice: '24.99',
          discountLabel: '37% OFF',
          imageUrl: '',
          showBadge: true,
        },
      },
      {
        type: 'cta-section',
        props: {
          acceptText: 'Add now',
          declineText: 'Dismiss',
          acceptColor: '#22c55e',
          declineColor: '#71717a',
        },
      },
    ],
  },
  {
    id: 'popup-scroll-capture',
    name: 'Popup: Scroll Capture',
    description: 'Popup layout for users who reached deep scroll position',
    thumbnail: 'ScrollText',
    blocks: [
      {
        type: 'hero',
        props: {
          headline: 'Still browsing? Unlock this deal',
          subheadline: 'You made it this far, take the upgrade at a special price',
          imageUrl: '',
          bgColor: '#f8fafc',
        },
      },
      {
        type: 'reviews-widget',
        props: {
          quote: '"Best impulse add-on I ever accepted."',
          author: 'Lana P.',
          rating: 5,
        },
      },
      {
        type: 'cta-section',
        props: {
          acceptText: 'Get the upgrade',
          declineText: 'Maybe later',
          acceptColor: '#14b8a6',
          declineColor: '#71717a',
        },
      },
    ],
  },
  {
    id: 'popup-exit-intent',
    name: 'Popup: Exit Intent',
    description: 'Last-chance popup with social proof and strong CTA',
    thumbnail: 'DoorOpen',
    blocks: [
      {
        type: 'hero',
        props: {
          headline: 'Before You Leave: One Last Offer',
          subheadline: 'Save more now before this window closes',
          imageUrl: '',
          bgColor: '#fff1f2',
        },
      },
      {
        type: 'product-specs',
        props: {
          feature1: 'Special leave-intent discount',
          feature2: 'Fast shipping with current order',
          feature3: 'Easy returns',
          iconColor: '#ef4444',
        },
      },
      {
        type: 'cta-section',
        props: {
          acceptText: 'Apply this deal',
          declineText: 'Close offer',
          acceptColor: '#ef4444',
          declineColor: '#71717a',
        },
      },
    ],
  },
]

export const EMPTY_TEMPLATE: LayoutTemplate = {
  id: 'empty',
  name: 'Start from Scratch',
  description: 'Build your page from the ground up with complete creative control',
  thumbnail: 'FileText',
  blocks: [],
}
